import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { buildVisitsChart } from '@configurations/campaignAnalytics/audienceProfileChart';
import { ChartCard } from '@components/shared/CampaignAnalytics/ChartCard/ChartCard';
import { EditableChartProps } from './chartProps';
import { useMemo, useState } from 'react';
import { Autocomplete, Box, TextField } from '@mui/material';
import { CampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { truncateAudienceGroupNames } from '@features/campaign-analytics';
import { sortBy } from '@utils/arrays';

type AudienceProfileProps = EditableChartProps;

export type AudienceSegment =
  CampaignAnalyticsReport['audienceProfile']['data']['visited'][number];

export const DIMENSIONS_PER_GRAPH = 10;

export const PREDICTIVE_GROUP_VALUE = 'Predictive';

const ALL_GROUP_VALUE = 'All';

const topDimensionsByField = (
  nameValuePairs: AudienceSegment[],
  selectedGroup: AudienceSegment['group']
) =>
  nameValuePairs
    .filter(
      pair => selectedGroup === ALL_GROUP_VALUE || pair.group === selectedGroup
    )
    .toSorted(sortBy('value', 'desc'))
    .slice(0, DIMENSIONS_PER_GRAPH);

const AudienceProfile = (props: AudienceProfileProps) => {
  const { report, ...chartCardProps } = props;

  const audienceGroups = useMemo(() => {
    const uniqueGroups = [
      ...new Set(
        report.audienceProfile.data.visited.map(
          audienceSegment => audienceSegment.group
        )
      ),
    ];
    return [ALL_GROUP_VALUE, ...uniqueGroups];
  }, [report.audienceProfile.data.visited]);

  const [selectedGroup, setSelectedGroup] = useState<string>(
    audienceGroups.includes(PREDICTIVE_GROUP_VALUE)
      ? 'Predictive'
      : ALL_GROUP_VALUE
  );

  if (!report) return null;

  const visitedAudienceProfile = topDimensionsByField(
    report.audienceProfile.data.visited,
    selectedGroup
  );

  const truncatedVisitedAudienceProfile = truncateAudienceGroupNames(
    visitedAudienceProfile
  );

  const allGroupsNull = props.report.audienceProfile.data.visited.every(
    item => item.group === null
  );
  const includedInReport = chartCardProps.includedInReport ?? true;

  return (
    <ChartCard
      title="Audience Profile (Saw the Ad and Visited)"
      description={`${props.campaignName} (${props.campaignId})`}
      report={report.audienceProfile}
      renderHeader={() => {
        return includedInReport && !allGroupsNull ? (
          <Box>
            <Autocomplete
              sx={{ width: 300 }}
              size="medium"
              options={audienceGroups}
              renderInput={params => (
                <TextField {...params} label="Search..." />
              )}
              onChange={(_, newValue) => {
                setSelectedGroup(newValue ?? '');
              }}
              getOptionLabel={option => option?.toString() ?? ''}
              value={selectedGroup}
              disableClearable
            />
          </Box>
        ) : null;
      }}
      {...chartCardProps}
    >
      <HighchartsReact
        highcharts={Highcharts}
        options={buildVisitsChart(truncatedVisitedAudienceProfile)}
      />
    </ChartCard>
  );
};

export default AudienceProfile;
